import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { AlertField } from '../config/alertFields';
// Icons replaced with emojis for compatibility

interface AlertRule {
  id: string;
  field: string;
  operator: '>' | '<' | '>=' | '<=' | '=' | '!=' | 'contains' | 'not_contains';
  value: string | number;
  enabled: boolean;
}

interface AlertSettings {
  rules: AlertRule[];
  soundEnabled: boolean;
  notificationEnabled: boolean;
  soundFile: string;
  volume: number;
  cooldownMinutes: number;
  newOpportunitiesOnly: boolean;
  alertOnStableFetch?: boolean;
  minPremiumFloor?: number;
  minVolumeFloor?: number;
  useBigFlowFloor?: boolean;
}

const fieldStyle: React.CSSProperties = {
  background: 'var(--elev2)',
  borderColor: 'var(--border)',
  color: 'var(--txt)',
};
const cardStyle: React.CSSProperties = {
  background: 'var(--elev2)',
  borderColor: 'var(--hair)',
};
const primaryBtnStyle: React.CSSProperties = {
  background: 'var(--acc)',
  color: 'var(--void)',
  borderColor: 'transparent',
};

interface AlertSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (settings: AlertSettings) => void;
  alertSettings: AlertSettings;
  availableFields: AlertField[];
}

const AlertSettingsModal: React.FC<AlertSettingsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  alertSettings,
  availableFields
}) => {
  const { isDarkMode } = useTheme();
  const [settings, setSettings] = useState<AlertSettings>(alertSettings);
  const [newRule, setNewRule] = useState<Partial<AlertRule>>({
    field: '',
    operator: '>',
    value: '',
    enabled: true
  });

  useEffect(() => {
    setSettings(alertSettings);
  }, [alertSettings]);

  const handleSave = () => {
    onSave(settings);
    onClose();
  };

  const addRule = () => {
    if (!newRule.field || newRule.value === '') return;

    const rule: AlertRule = {
      id: Date.now().toString(),
      field: newRule.field,
      operator: newRule.operator || '>',
      value: newRule.value,
      enabled: newRule.enabled || true
    };

    setSettings(prev => ({
      ...prev,
      rules: [...prev.rules, rule]
    }));

    setNewRule({
      field: '',
      operator: '>',
      value: '',
      enabled: true
    });
  };

  const removeRule = (id: string) => {
    setSettings(prev => ({
      ...prev,
      rules: prev.rules.filter(rule => rule.id !== id)
    }));
  };

  const updateRule = (id: string, updates: Partial<AlertRule>) => {
    setSettings(prev => ({
      ...prev,
      rules: prev.rules.map(rule =>
        rule.id === id ? { ...rule, ...updates } : rule
      )
    }));
  };

  const getOperatorsForField = (fieldKey: string) => {
    const field = availableFields.find(f => f.key === fieldKey);
    if (!field) return [];

    switch (field.type) {
      case 'number':
        return [
          { value: '>', label: 'Greater than' },
          { value: '<', label: 'Less than' },
          { value: '>=', label: 'Greater than or equal' },
          { value: '<=', label: 'Less than or equal' },
          { value: '=', label: 'Equals' },
          { value: '!=', label: 'Not equals' }
        ];
      case 'string':
        return [
          { value: '=', label: 'Equals' },
          { value: '!=', label: 'Not equals' },
          { value: 'contains', label: 'Contains' },
          { value: 'not_contains', label: 'Does not contain' }
        ];
      case 'boolean':
        return [
          { value: '=', label: 'Equals' },
          { value: '!=', label: 'Not equals' }
        ];
      default:
        return [];
    }
  };

  const getValueInputType = (fieldKey: string) => {
    const field = availableFields.find(f => f.key === fieldKey);
    if (!field) return 'text';

    switch (field.type) {
      case 'number':
        return 'number';
      case 'boolean':
        return 'checkbox';
      default:
        return 'text';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        style={{
          background: 'var(--elev)',
          color: 'var(--txt)',
          border: '1px solid var(--hair)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--lift-3)',
          fontFamily: 'var(--font-ui)',
          fontSize: 13,
          colorScheme: isDarkMode ? 'dark' : 'light',
        }}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-semibold" style={{ fontSize: 18 }}>Alert Settings</h2>
            <button
              onClick={onClose}
              className="mmec-btn is-ghost"
            >
              ×
            </button>
          </div>

          {/* Sound Settings */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-3">Sound Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="soundEnabled"
                  checked={settings.soundEnabled}
                  onChange={(e) => setSettings(prev => ({ ...prev, soundEnabled: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="soundEnabled">Enable Alert Sounds</label>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Sound File</label>
                <select
                  value={settings.soundFile}
                  onChange={(e) => setSettings(prev => ({ ...prev, soundFile: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md"
                  style={fieldStyle}
                >
                  <option value="bell-notification-337658.mp3">Bell Notification</option>
                  <option value="bell-sound-370341.mp3">Bell Sound</option>
                  <option value="level-up-02-199574.mp3">Level Up</option>
                  <option value="new-notification-08-352461.mp3">New Notification</option>
                  <option value="new-notification-09-352705.mp3">New Notification 2</option>
                  <option value="notification-bell-sound-376888.mp3">Notification Bell</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Volume</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={settings.volume}
                  onChange={(e) => setSettings(prev => ({ ...prev, volume: parseInt(e.target.value) }))}
                  className="w-full"
                />
                <span className="text-xs" style={{ color: 'var(--muted)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>{settings.volume}%</span>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Cooldown (minutes)</label>
                <input
                  type="number"
                  min="0"
                  max="60"
                  value={settings.cooldownMinutes}
                  onChange={(e) => setSettings(prev => ({ ...prev, cooldownMinutes: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded-md"
                  style={fieldStyle}
                />
              </div>
            </div>
          </div>

          {/* Stable fetch + big flow */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-3">After stable fetch (SPX, etc.)</h3>
            <p className="mb-3" style={{ fontSize: 12.5, color: 'var(--muted)' }}>
              When you fetch a stable chain snapshot, scan smart flow and notify on large liquidity prints.
              Custom rules below override the default floor when any rule is enabled.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="alertOnStableFetch"
                  checked={settings.alertOnStableFetch !== false}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      alertOnStableFetch: e.target.checked,
                    }))
                  }
                  className="mr-2"
                />
                <label htmlFor="alertOnStableFetch">Alert after stable fetch</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="useBigFlowFloor"
                  checked={settings.useBigFlowFloor !== false}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      useBigFlowFloor: e.target.checked,
                    }))
                  }
                  className="mr-2"
                />
                <label htmlFor="useBigFlowFloor">Use big-flow floor (when no rules)</label>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Min premium (USD)</label>
                <input
                  type="number"
                  min={0}
                  step={10000}
                  value={settings.minPremiumFloor ?? 250000}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      minPremiumFloor: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-md"
                  style={fieldStyle}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Min volume (contracts)</label>
                <input
                  type="number"
                  min={0}
                  step={100}
                  value={settings.minVolumeFloor ?? 0}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      minVolumeFloor: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-md"
                  style={fieldStyle}
                />
              </div>
            </div>
          </div>

          {/* Notification Settings */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-3">Notification Settings</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="notificationEnabled"
                  checked={settings.notificationEnabled}
                  onChange={(e) => setSettings(prev => ({ ...prev, notificationEnabled: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="notificationEnabled">Enable Browser Notifications</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="newOpportunitiesOnly"
                  checked={settings.newOpportunitiesOnly || false}
                  onChange={(e) => setSettings(prev => ({ ...prev, newOpportunitiesOnly: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="newOpportunitiesOnly">Alert only for new opportunities (exclude existing contracts)</label>
              </div>
            </div>
          </div>

          {/* Alert Rules */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-3">Alert Rules</h3>
            
            {/* Existing Rules */}
            <div className="space-y-3 mb-4">
              {settings.rules.map((rule) => (
                <div key={rule.id} className="p-3 rounded-lg border" style={cardStyle}>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={rule.enabled}
                      onChange={(e) => updateRule(rule.id, { enabled: e.target.checked })}
                    />
                    <select
                      value={rule.field}
                      onChange={(e) => updateRule(rule.id, { field: e.target.value })}
                      className="px-2 py-1 border rounded"
                      style={fieldStyle}
                    >
                      <option value="">Select Field</option>
                      {availableFields.map(field => (
                        <option key={field.key} value={field.key}>{field.label}</option>
                      ))}
                    </select>
                    <select
                      value={rule.operator}
                      onChange={(e) => updateRule(rule.id, { operator: e.target.value as any })}
                      className="px-2 py-1 border rounded"
                      style={fieldStyle}
                    >
                      {getOperatorsForField(rule.field).map(op => (
                        <option key={op.value} value={op.value}>{op.label}</option>
                      ))}
                    </select>
                    <input
                      type={getValueInputType(rule.field)}
                      value={rule.value}
                      onChange={(e) => updateRule(rule.id, { value: e.target.value })}
                      className="px-2 py-1 border rounded"
                      style={fieldStyle}
                      placeholder="Value"
                    />
                    <button
                      onClick={() => removeRule(rule.id)}
                      className="mmec-btn is-ghost"
                      style={{ color: 'var(--down)' }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add New Rule */}
            <div className="p-3 rounded-lg border" style={cardStyle}>
              <h4 className="text-sm font-semibold mb-3">Add New Rule</h4>
              <div className="flex items-center gap-3">
                <select
                  value={newRule.field}
                  onChange={(e) => setNewRule(prev => ({ ...prev, field: e.target.value }))}
                  className="px-2 py-1 border rounded"
                      style={fieldStyle}
                >
                  <option value="">Select Field</option>
                  {availableFields.map(field => (
                    <option key={field.key} value={field.key}>{field.label}</option>
                  ))}
                </select>
                <select
                  value={newRule.operator}
                  onChange={(e) => setNewRule(prev => ({ ...prev, operator: e.target.value as any }))}
                  className="px-2 py-1 border rounded"
                      style={fieldStyle}
                >
                  {getOperatorsForField(newRule.field || '').map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
                <input
                  type={getValueInputType(newRule.field || '')}
                  value={newRule.value}
                  onChange={(e) => setNewRule(prev => ({ ...prev, value: e.target.value }))}
                  className="px-2 py-1 border rounded"
                      style={fieldStyle}
                  placeholder="Value"
                />
                <button
                  onClick={addRule}
                  className="mmec-btn"
                  style={primaryBtnStyle}
                >
                  + Add
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="mmec-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="mmec-btn"
              style={primaryBtnStyle}
            >
              💾 Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertSettingsModal;
